<div mat-dialog-title fxLayoutAlign="space-between center">
  <h2>{{ 'dialog.charges.add-charges' | translate }}</h2>
</div>

<mat-progress-bar [mode]="(loading$ | async) ? 'indeterminate' : 'determinate'"></mat-progress-bar>

<form [formGroup]="estimateForm" (submit)="submitEstimate()" autocomplete="off">
  <mat-dialog-content fxLayout="column" fxLayoutGap="10px">
    <div fxLayout="row" fxLayoutAlign="space-between start" fxLayoutGap="1rem">
      <mat-form-field fxFlex subscriptSizing="dynamic">
        <mat-label>{{ 'common.appointments' | translate }}</mat-label>
        <mat-select formControlName="appointmentIds" multiple>
          <mat-option *ngFor="let appointment of appointments$ | async"
                      [value]="appointment.id"
                      [disabled]="appointment.id === data.appointmentId"
                      [matTooltip]="getProductsName(appointment.products)">
            <span *ngIf="appointment.startAt; else order">{{ appointment.startAt | momentDateTime }}</span>
            <ng-template #order><span>{{ 'common.order' | translate }}</span></ng-template>
            <span *ngIf="appointment.products" class="ml-2">({{ getProductsName(appointment.products) }})</span>
            <span class="text-warn" *ngIf="appointment.estimate"> - {{ 'dialog.charges.appt-included' | translate }} </span>
          </mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field fxFlex subscriptSizing="dynamic">
        <mat-label>{{ 'common.facility' | translate }}</mat-label>
        <mmx-facility-selector-input formControlName="facilityId" required></mmx-facility-selector-input>
      </mat-form-field>
    </div>
    <div fxLayout="row" fxLayoutAlign="space-between start" fxLayoutGap="1rem">
      <mat-form-field fxFlex subscriptSizing="dynamic">
        <mat-label>{{ 'common.template' | translate }}</mat-label>
        <mat-select formControlName="templateId">
          <mat-option>
            <ngx-mat-select-search
              [placeholderLabel]="'common.template' | translate"
              [noEntriesFoundLabel]="'dialog.charges.not-found' | translate"
              [formControl]="filterTemplateCtrl"
            >
            </ngx-mat-select-search>
          </mat-option>
          <mat-option value=""> {{ 'common.new' | translate }}</mat-option>
          <mat-option *ngFor="let template of templates$ | async" [value]="template.id">
            <span>{{ template.name }}</span>
          </mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field fxFlex *ngIf="hbEnabled$ | async" subscriptSizing="dynamic">
        <mat-label>{{ 'common.patient-type' | translate }}</mat-label>
        <mat-select formControlName="patientType">
          <mat-option value="I">
            <span>{{ 'dialog.charges.patient-type.inpatient' | translate }}</span>
          </mat-option>
          <mat-option value="O">
            <span>{{ 'dialog.charges.patient-type.outpatient' | translate }}</span>
          </mat-option>
        </mat-select>
      </mat-form-field>
    </div>
    <ng-container *ngIf="clinic.pricer.claimSearch">
      <mmx-model-claim-search
        *mmxHasAccess="'estimate.model-claim-search'"
        [facilityId]="estimateForm.value.facilityId"
        [serviceType]="estimateForm.value.patientType"
        fxFlex
        (modelClaim)="addModelClaim($event)"
      ></mmx-model-claim-search>
    </ng-container>
    <mat-card>
      <mat-card-header fxLayoutAlign="space-between center">
        <mat-card-title>
          {{ 'dialog.charges.charges' | translate }}
        </mat-card-title>
        <button
          mat-stroked-button
          [matMenuTriggerFor]="chargesMenu"
          [matTooltip]="'dialog.charges.reset.tooltip' | translate"
          (click)="$event.preventDefault()"
        >
          <mat-icon>restart_alt</mat-icon>
          <span>{{ 'common.reset' | translate }}</span>
        </button>
        <mat-menu #chargesMenu="matMenu">
          <button
            mat-menu-item
            (click)="resetEstimateForm()"
          >
            <span> {{ 'dialog.charges.reset.by-appointment' | translate }}</span>
          </button>
          <button
            mat-menu-item
            *ngIf="estimate?.templateId || estimateForm.value.templateId"
            (click)="estimateForm.get('templateId').setValue(estimateForm.value.templateId || estimate?.templateId)"
          >
            <span> {{ 'dialog.charges.reset.by-template' | translate }}</span>
          </button>
          <button
            mat-menu-item
            *ngIf="estimate?.id"
            (click)="resetEstimateForm('estimate')"
          >
            <span> {{ 'dialog.charges.reset.by-estimate' | translate }}</span>
          </button>
          <button
            mat-menu-item
            color="warn"
            (click)="resetEstimateForm('clear')"
          >
            <mat-icon color="warn">clear</mat-icon>
            <span> {{ 'dialog.charges.reset.all' | translate }}</span>
          </button>
        </mat-menu>
      </mat-card-header>
      <mat-card-content>
        <mmx-charges-table-form [facilityId]="estimateForm.value.facilityId" formControlName="charges"></mmx-charges-table-form>
      </mat-card-content>
    </mat-card>
  </mat-dialog-content>
  <mat-error *ngIf="errorMessage" class="p-4">
    {{ errorMessage | titlecase }}
  </mat-error>
  <mat-dialog-actions fxLayout="row" fxLayoutAlign="space-between center">
    <button
      mat-stroked-button
      type="button"
      [disabled]="loading$ | async"
      mat-dialog-close>
      {{ 'common.cancel' | translate }}
    </button>
    <div fxLayout="row" fxLayoutAlign="space-between center" fxLayoutGap="10px">
      <button
        mat-stroked-button
        color="warn"
        *ngIf="estimate"
        (click)="deleteEstimate()"
        [disabled]="loading$ | async"
      >
        <mat-icon color="warn">delete</mat-icon>
        {{ 'common.delete' | translate }}
      </button>
      <div>
        <ng-container *appHasPermission="['estimate-template.create', 'estimate-template.edit']; else noPermission">
          <mat-button-toggle-group [disabled]="loading$ | async">
            <mat-button-toggle type class="split-button-1" (click)="submitEstimate()">
              <ng-container *ngIf="estimateForm.value.saveTemplate">
                {{ 'dialog.charges.save-charges-and-template' | translate }}
              </ng-container>
              <ng-container *ngIf="!estimateForm.value.saveTemplate">
                {{ 'dialog.charges.save-charges' | translate }}
              </ng-container>
            </mat-button-toggle>
            <mat-button-toggle class="split-button-1 drop-down-button" [matMenuTriggerFor]="menu">
              <mat-icon>expand_more</mat-icon>
            </mat-button-toggle>
          </mat-button-toggle-group>
        </ng-container>
        <ng-template #noPermission>
          <button
            type="submit"
            mat-flat-button
            class="full-width"
            color="primary"
            [disabled]="loading$ | async"
          >
            {{ 'dialog.charges.save-charges' | translate }}
          </button>
        </ng-template>


        <mat-menu #menu="matMenu">
          <div class="container" fxLayout="column" fxLayoutGap="1rem" (click)="$event.stopPropagation()">
            <mat-slide-toggle
              fxFlex
              formControlName="saveTemplate"
              color="primary"
            >
              <ng-container *ngIf="estimateForm.value.templateId; else templateEmpty">
                {{ 'dialog.charges.update-template' | translate }}
              </ng-container>
              <ng-template #templateEmpty>
                {{ 'dialog.charges.save-template' | translate }}
              </ng-template>
            </mat-slide-toggle>
            <mat-form-field>
              <mat-label>{{ 'dialog.charges.template-name' | translate }}</mat-label>
              <input matInput type="text" formControlName="name">
            </mat-form-field>
          </div>
        </mat-menu>
      </div>
    </div>
  </mat-dialog-actions>
</form>
